import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import "./style.scss";
import {
  Button,
  Heading,
  HStack,
  Center,
  Container,
  useToast,
  Grid,
  Checkbox,
  Box,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { InputText, Select, DatePicker, YearPickerInput } from "../../../components/Input";
import { QuestionnaireHeaderNew } from "../../../components/Form";
import {
  periodicityOptions,
} from "../../../resources/mock_options";
import { periodicCreateSchema } from "../../../core/validations/periodic";
import { fetchData } from "../../../services/fetch";
import { CustomTab } from "../../../components/Tabs";
import baseService from "../../../services/base";
import { format } from "date-fns";
import { SuccessModal } from "../../../components/Modals";
// EG-42/AD-2: a estrutura deixou de ser digitada e passou a ser derivada dos
// templates. PillarConfigRow (número de pilares + pilar por linha) e
// PillarTabEdit (nome de tema em texto livre) saíram junto com o fluxo antigo.
import SelecaoDeTemplates from "../../../components/Questionnaires/SelecaoDeTemplates";
import EstruturaDerivada from "../../../components/Questionnaires/EstruturaDerivada";
import {
  derivarEstruturaDeTemplates,
  idsParaGravar,
} from "../../../core/estrutura/derivarEstruturaDeTemplates";
import { useCatalogoDeClassificacao } from "../../../hooks/useCatalogoDeClassificacao";

export default function PeriodicRegister() {
  const toast = useToast();
  const navigate = useNavigate();
  const [startDeadline, setStartDeadline] = useState();
  const [endDeadline, setEndDeadline] = useState();
  const [noEndDeadline, setNoEndDeadline] = useState(false);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [showPillarsTabs, setShowPillarsTabs] = useState(false);
  // EG-42/AD-3: as questões dos templates escolhidos, na ORDEM de seleção — essa
  // ordem é o que vira o index global no backend (AD-6).
  const [questoesSelecionadas, setQuestoesSelecionadas] = useState([]);

  // Remover aqui é só NÃO criar a questão — nada a ver com a AD-9, que trata de
  // classificação de questão já existente. O botão vem do mesmo card da edição.
  const removerQuestao = (idQuestionTemplate) =>
    setQuestoesSelecionadas((atuais) =>
      atuais.filter((q) => q.id_question_template !== idQuestionTemplate)
    );
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [exercise, setExercise] = useState(null);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    // EG-42/AD-2: schema único de criação. A estrutura (pilares, temas,
    // perguntas) deixou de ser campo do formulário e virou derivação dos
    // templates — exigi-la aqui deixava isValid falso para sempre.
    resolver: yupResolver(periodicCreateSchema),
    mode: "onChange",
  });

  const { data: templates, isLoading: templatesIsLoading } = useSWR(
    `/survey-template/all`,
    fetchData
  );

  // O catálogo global dá nome ao pilar e título ao tema. Enquanto ele não chega,
  // derivarEstruturaDeTemplates mostra tudo em "Sem classificação" em vez de
  // sumir com as questões.
  const catalogo = useCatalogoDeClassificacao();

  const estrutura = derivarEstruturaDeTemplates({
    questoes: questoesSelecionadas,
    pilares: catalogo?.pilares,
    temas: catalogo?.temas,
    // Periódico: a hierarquia é pilar > tema > questão, então questão sem tema
    // não tem sanfona onde ficar e cai em "Sem classificação".
    exigeTema: true,
  });

  function handlePillarsConfig() {
    setShowPillarsTabs(true);
    setTabIndex(1);
  }

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  const showErrorToast = (message) => {
    toast({
      title: "Erro",
      description: message,
      status: "error",
    });
  };

  const onSubmit = async (data) => {
    const start = format(new Date(data?.startDeadline), "yyyy-MM");

    try {
      const surveyResponse = await baseService.post(`/survey/periodic`, {
        description: data.name,
        surveyQuestions: [],
        start_date: start,
        ...(!data?.noEndDeadline && {
          end_date: format(new Date(data.endDeadline), "yyyy-MM"),
        }),
        type_period: selectedPeriodicity,
        ...(exercise ? { exercise: exercise.getFullYear() } : {}),
      });

      const idSurvey = surveyResponse?.data?.survey?.id_survey;

      // EG-42/AD-2: a estrutura é montada pelo caminho que HERDA a classificação
      // do template.
      //
      // O laço antigo postava /survey/periodic/pillar por pilar, com id_pillar e
      // temas digitados na tela. Dois problemas: ele mandava theme.idTheme, que
      // nenhuma parte do formulário preenchia — o fluxo está quebrado desde a
      // EG-40 e nenhum periódico foi criado desde 2026-05-08; e aquele caminho
      // exige pilar e tema do catálogo, então questão de template SEM
      // classificação não teria como entrar (9.748 de 9.754 hoje).
      //
      // A ordem de idsParaGravar é a ordem que o gestor está vendo, e é ela que
      // o backend transforma em index global (AD-6).
      await baseService.post(`/survey/${idSurvey}/questions/from-templates`, {
        id_question_templates: idsParaGravar(estrutura),
      });

      setIsSuccessModalOpen(true);
    } catch (error) {
      showErrorToast("Não foi possível salvar o questionário");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <QuestionnaireHeaderNew
        heading="Cadastrar Questionário"
        isSubmitting={isSubmitting}
        isSubmitEnabled={
          isValid &&
          showPillarsTabs &&
          selectedPeriodicity &&
          questoesSelecionadas.length > 0
        }
        isSubmit={true}
      >
        <Flex gap="4" flexWrap="wrap" alignItems="baseline">
          <HStack minW="360px" w="500px">
            <InputText
              {...register("name")}
              error={errors.name}
              placeholder="Digite o nome do questionário"
              autoComplete="off"
            />
          </HStack>

          <YearPickerInput
            value={exercise}
            onChange={(date) => setExercise(date)}
          />

          <Grid templateColumns="220px 220px 220px 140px" gap={4}>
            <Select
              name="periodicity"
              placeholder="Tipo de periodicidade"
              options={periodicityOptions}
              onChange={(e) => setSelectedPeriodicity(e.target.value)}
            />
            <DatePicker
              control={control}
              name="startDeadline"
              defaultValue={startDeadline}
              placeholder="Prazo inicial"
              selectedDate={startDeadline}
              onChange={(date) => {
                setStartDeadline(date);
                trigger("endDeadline");
              }}
              disabled={!selectedPeriodicity}
            />
            <DatePicker
              control={control}
              name="endDeadline"
              defaultValue={endDeadline}
              placeholder="Prazo final"
              selectedDate={endDeadline}
              minDate={startDeadline}
              onChange={(date) => setEndDeadline(date)}
              disabled={!selectedPeriodicity || noEndDeadline || !startDeadline}
              error={errors.endDeadline}
            />
            <Checkbox
              {...register("noEndDeadline")}
              onChange={(e) => {
                setNoEndDeadline(e.target.checked);
                setValue("noEndDeadline", e.target.checked);
                trigger("endDeadline");
              }}
              className="regular-checkbox"
            >
              Sem prazo final
            </Checkbox>
          </Grid>
        </Flex>
      </QuestionnaireHeaderNew>

      <Center my={["8px", "32px"]} px={{ xl: "86px" }}>
        <Container maxW={"1280px"} p={0} minH="80vh">
          <Box bg="white" py="6" borderRadius="12px">
            <Tabs index={tabIndex} onChange={handleTabsChange}>
              <TabList px="6" border="none" userSelect="none">
                <CustomTab>Configurações gerais</CustomTab>
                {showPillarsTabs && <CustomTab>Perguntas e estrutura</CustomTab>}
              </TabList>
              <TabPanels>
                <TabPanel p="6">
                  <Stack spacing={6} pt="6">
                    {tabIndex === 0 && (
                      // EG-42/AD-2: botão próprio, não o SaveButton compartilhado.
                      //
                      // Aquele componente observa o array `form` (os pilares
                      // configurados à mão) e desabilita quando ele está vazio —
                      // e a inversão APAGOU esse campo. Resultado: o botão nunca
                      // habilitava e a criação de periódico não chegava na aba de
                      // estrutura. Mesmo defeito dos schemas, uma camada mais
                      // fundo: um controle validando o campo que a inversão tirou.
                      //
                      // Não mexi no SaveButton porque Periodic/Edit/Form.jsx e
                      // Diagnosis/ConfigGeneralSection.jsx dependem dele com o
                      // array `form` presente.
                      <Button
                        aria-label="Salvar"
                        variant="primary"
                        w="122px"
                        size="medium"
                        ml="auto"
                        onClick={handlePillarsConfig}
                        isDisabled={!isValid || !selectedPeriodicity}
                      >
                        Salvar
                      </Button>
                    )}
                  </Stack>
                </TabPanel>

                {/*
                  EG-42/AD-2: a inversão. O "Número de pilares" e o
                  PillarConfigRow saíram — o gestor não declara mais a estrutura.
                  Ele escolhe templates e vê pilares, temas e a seção "Sem
                  classificação" nascerem da classificação das perguntas.
                */}
                {showPillarsTabs && (
                  <TabPanel py="6" px="0">
                    {/* Segue o padrão da própria tela de edição do periódico:
                        SEM cartão branco aqui, porque o painel inteiro já vive
                        dentro de um. Cabeçalho com borda e conteúdo com padding. */}
                    <Heading
                      fontSize="20px"
                      fontWeight={600}
                      lineHeight="23.48px"
                      p="6"
                      borderBottom="2px solid #F5F5FB"
                    >
                      Templates
                    </Heading>

                    <Stack spacing={8} p="6">
                      <SelecaoDeTemplates
                        templates={templates}
                        templatesIsLoading={templatesIsLoading}
                        questoesSelecionadas={questoesSelecionadas}
                        onChange={setQuestoesSelecionadas}
                        isDisabled={isSubmitting}
                      />
                      <Box borderTop='2px solid #F5F5FB' pt='6' mx='-6'>
                        <EstruturaDerivada
                          estrutura={estrutura}
                          exigeTema
                          onRemoverQuestao={removerQuestao}
                        />
                      </Box>
                    </Stack>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Box>
          <SuccessModal
            isOpen={isSuccessModalOpen}
            onClose={() => {
              setIsSuccessModalOpen(false);
              navigate("/researches");
            }}
            title="Questionário criado com sucesso!"
          />
        </Container>
      </Center>
    </form>
  );
}
